import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import style from './style.module.css'

import Companies from './components/Companies'
import CompanyModal from './components/CompanyModal'
import Filter from './components/Filter'
import FilterHeader from './components/FilterHeader'
import Statistic from './components/Statistic'

import useWindowWidth from '../../../hooks/useWindowWidth'
import useDebouncedValue from '../../../hooks/useDebouncedValue'

import {
  fetchCompanies,
  fetchCompany,
  fetchFilterOptions,
  fetchStats,
  setCompanySaved,
  isForbidden,
  isUnauthorized,
} from './api'

const PAGE_SIZE = 24

/**
 * Spotlight is a careers fair, so the default population is employers.
 * Universities, municipalities and publishers are classified rather than
 * removed — they are one facet click away.
 */
export const DEFAULT_ORG_KINDS = ['company']

export const EMPTY_FILTERS = {
  search: '',
  orgKinds: DEFAULT_ORG_KINDS,
  domainKeys: [],
  countryCodes: [],
  cities: [],
  roleFamilyKeys: [],
  contentTypes: [],
  sizeTiers: [],
  remote: false,
  hiring: false,
  savedOnly: false,
  allLocations: false,
  sort: 'relevance',
}

/** The multi-select facet groups, in the order the filter rail renders them. */
export const FACET_GROUPS = [
  { key: 'orgKinds', optionsKey: 'orgKinds', label: 'Organisation type', icon: 'company' },
  { key: 'domainKeys', optionsKey: 'domains', label: 'Industry', icon: 'industry' },
  { key: 'countryCodes', optionsKey: 'countries', label: 'Location', icon: 'pin' },
  { key: 'sizeTiers', optionsKey: 'sizeTiers', label: 'Company size', icon: 'people' },
  { key: 'contentTypes', optionsKey: 'contentTypes', label: 'Offers', icon: 'bag' },
  { key: 'roleFamilyKeys', optionsKey: 'roleFamilies', label: 'Hiring for', icon: 'bag2' },
]

/** True when orgKinds has been changed away from the default. */
const orgKindsChanged = (kinds) =>
  !Array.isArray(kinds) ||
  kinds.length !== DEFAULT_ORG_KINDS.length ||
  !DEFAULT_ORG_KINDS.every((k) => kinds.includes(k))

const countActiveFilters = (filters) =>
  FACET_GROUPS.reduce(
    (total, group) =>
      // orgKinds always has a value, so counting it raw would show "1 filter
      // active" on a page nobody has touched.
      group.key === 'orgKinds' ? total : total + (filters[group.key]?.length || 0),
    0
  ) +
  (orgKindsChanged(filters.orgKinds) ? 1 : 0) +
  (filters.remote ? 1 : 0) +
  (filters.hiring ? 1 : 0) +
  (filters.savedOnly ? 1 : 0) +
  (filters.allLocations ? 1 : 0) +
  (filters.search ? 1 : 0)

const Spotlight = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useWindowWidth() <= 768

  // ?org=<key> opens a booth directly. Two reasons this matters: booths become
  // linkable and shareable, and a booth that exists only as authored content —
  // the fictional showcase company has no registry row and so never appears in
  // the grid — is otherwise unreachable through the UI.
  const deepLinkOrg = new URLSearchParams(location.search).get('org')

  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const debouncedSearch = useDebouncedValue(filters.search, 380)

  const [companies, setCompanies] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)

  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  const [options, setOptions] = useState(null)
  const [optionsFailed, setOptionsFailed] = useState(false)
  const [stats, setStats] = useState(null)

  const [activeCompany, setActiveCompany] = useState(null)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const [reloadToken, setReloadToken] = useState(0)
  const [sideToken, setSideToken] = useState(0)

  const listRequest = useRef(null)

  // How many rows the SERVER has handed us for the current filter set. Kept in a
  // ref rather than state because it must stay exact even when a row is removed
  // from the rendered list (un-saving in "saved only" mode) — paging is a
  // property of the query, not of what happens to be on screen.
  const serverOffset = useRef(0)

  // Incremented on every filter change. A load-more response that comes back
  // carrying an older generation belongs to the previous filter set and must be
  // discarded, or it appends companies that do not match the current filters.
  const generation = useRef(0)

  const appliedFilters = useMemo(
    () => ({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch]
  )

  // Serialised so the effect re-runs on a real filter change rather than on
  // every render (the objects and arrays are new identities each time).
  const filtersKey = JSON.stringify(appliedFilters)

  const handleAuthError = useCallback(
    (err) => {
      if (isUnauthorized(err)) {
        navigate('/login', { replace: true })
        return true
      }
      if (isForbidden(err)) {
        setError(err)
        return true
      }
      return false
    },
    [navigate]
  )

  // ---- list -------------------------------------------------------------
  useEffect(() => {
    listRequest.current?.abort()
    const controller = new AbortController()
    listRequest.current = controller

    generation.current += 1
    const requestGeneration = generation.current

    setLoading(true)
    setError(null)

    fetchCompanies({
      filters: appliedFilters,
      limit: PAGE_SIZE,
      offset: 0,
      signal: controller.signal,
    })
      .then((data) => {
        if (controller.signal.aborted || generation.current !== requestGeneration) return
        setCompanies(data.items)
        setTotalCount(data.totalCount)
        setHasMore(data.hasMore)
        serverOffset.current = data.items.length
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        if (isUnauthorized(err)) {
          handleAuthError(err)
          return
        }
        setCompanies([])
        setTotalCount(0)
        setHasMore(false)
        serverOffset.current = 0
        setError(err)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => controller.abort()
    // appliedFilters is covered by filtersKey; including it would re-run every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, reloadToken, handleAuthError])

  // ---- filter options + stats -------------------------------------------
  // Both are retryable via sideToken: a single transient failure used to leave
  // the rail and the stats strip empty for the rest of the session.
  useEffect(() => {
    const controller = new AbortController()

    setOptionsFailed(false)

    fetchFilterOptions({ signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return
        setOptions(data)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        // The list is still usable without facets, so this only degrades the
        // filter rail. 401/403 are still escalated.
        if (!handleAuthError(err)) {
          console.error('Spotlight filter options:', err.message)
          setOptionsFailed(true)
        }
      })

    fetchStats({ signal: controller.signal })
      .then((data) => !controller.signal.aborted && setStats(data))
      .catch((err) => {
        if (controller.signal.aborted) return
        if (!handleAuthError(err)) console.error('Spotlight stats:', err.message)
      })

    return () => controller.abort()
  }, [handleAuthError, sideToken])

  // ---- load more --------------------------------------------------------
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return

    const requestGeneration = generation.current
    const requestOffset = serverOffset.current

    setLoadingMore(true)
    setNotice(null)

    fetchCompanies({ filters: appliedFilters, limit: PAGE_SIZE, offset: requestOffset })
      .then((data) => {
        // The filters changed while this was in flight; these rows belong to a
        // query the user has already moved on from.
        if (generation.current !== requestGeneration) return

        serverOffset.current = requestOffset + data.items.length
        setHasMore(data.hasMore)

        // An empty page must not overwrite the total: the server derives
        // totalCount from the returned window, so a zero-row page reports 0.
        if (data.items.length > 0) setTotalCount(data.totalCount)

        setCompanies((prev) => {
          const seen = new Set(prev.map((c) => c.orgKey))
          return [...prev, ...data.items.filter((c) => !seen.has(c.orgKey))]
        })
      })
      .catch((err) => {
        if (generation.current !== requestGeneration) return
        // Keep the rows already on screen; surface the failure inline instead.
        if (!handleAuthError(err)) setNotice(err.message)
      })
      .finally(() => setLoadingMore(false))
  }, [appliedFilters, hasMore, loadingMore, handleAuthError])

  // ---- filter mutations -------------------------------------------------
  const setSearch = useCallback((search) => setFilters((prev) => ({ ...prev, search })), [])

  const toggleFacet = useCallback((groupKey, value) => {
    setFilters((prev) => {
      const current = prev[groupKey] || []
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]
      return { ...prev, [groupKey]: next }
    })
  }, [])

  const setFlag = useCallback(
    (name, value) => setFilters((prev) => ({ ...prev, [name]: value })),
    []
  )

  const clearFilters = useCallback(() => setFilters(EMPTY_FILTERS), [])

  // Mobile segmented control: All / Saved / Hiring.
  const quickFilter = filters.savedOnly ? 'saved' : filters.hiring ? 'hiring' : 'all'
  const setQuickFilter = useCallback((choice) => {
    setFilters((prev) => ({
      ...prev,
      savedOnly: choice === 'saved',
      hiring: choice === 'hiring',
    }))
  }, [])

  // ---- saving -----------------------------------------------------------
  const handleToggleSave = useCallback(
    async (company) => {
      const nextSaved = !company.saved

      const applyLocally = (saved) => {
        setCompanies((prev) =>
          prev.map((c) => (c.orgKey === company.orgKey ? { ...c, saved } : c))
        )
        setActiveCompany((prev) =>
          prev && prev.orgKey === company.orgKey ? { ...prev, saved } : prev
        )
      }

      applyLocally(nextSaved) // optimistic
      setNotice(null)

      try {
        const result = await setCompanySaved({ orgKey: company.orgKey, saved: nextSaved })
        setStats((prev) => (prev ? { ...prev, savedCount: result.savedCount } : prev))

        // Un-saving while viewing only saved companies drops the row. The server
        // offset must come down with it, otherwise the next "Load more" starts
        // one row further along than the list actually reaches and silently
        // skips a company.
        if (!nextSaved && filters.savedOnly) {
          setCompanies((prev) => prev.filter((c) => c.orgKey !== company.orgKey))
          setTotalCount((prev) => Math.max(0, prev - 1))
          serverOffset.current = Math.max(0, serverOffset.current - 1)
        }
      } catch (err) {
        applyLocally(company.saved) // roll back
        // A failed save used to revert the bookmark with no explanation at all,
        // which reads as the button being broken.
        if (!handleAuthError(err)) setNotice(err.message)
      }
    },
    [filters.savedOnly, handleAuthError]
  )

  // ---- modal ------------------------------------------------------------
  const handleOpenCompany = useCallback(
    (company) => {
      setActiveCompany(company)
      // Make the open booth linkable without adding a history entry per click.
      navigate(`${location.pathname}?org=${encodeURIComponent(company.orgKey)}`, { replace: true })
    },
    [navigate, location.pathname]
  )

  const handleCloseCompany = useCallback(() => {
    setActiveCompany(null)
    navigate(location.pathname, { replace: true })
  }, [navigate, location.pathname])

  // Resolve ?org= on arrival. The booth may not be in the loaded page — or in
  // the list at all — so this fetches it directly rather than searching state.
  const resolvedDeepLink = useRef(null)
  useEffect(() => {
    if (!deepLinkOrg || resolvedDeepLink.current === deepLinkOrg) return
    resolvedDeepLink.current = deepLinkOrg

    const controller = new AbortController()

    fetchCompany({ orgKey: deepLinkOrg, signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted || !data.company) return
        setActiveCompany(data.company)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        // A bad link should not take the page down — drop the param and carry on.
        if (!handleAuthError(err)) {
          console.error('Spotlight deep link:', err.message)
          setNotice(`Could not open that company — ${err.message}`)
          navigate(location.pathname, { replace: true })
        }
      })

    return () => controller.abort()
  }, [deepLinkOrg, handleAuthError, navigate, location.pathname])

  // Lock background scrolling while the modal is open.
  useEffect(() => {
    if (!activeCompany) return undefined
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [activeCompany])

  const activeFilterCount = countActiveFilters(filters)

  // A 403 means the account is not allowed here at all; nothing on the page
  // would work, so replace the whole view rather than showing an empty grid.
  if (error && isForbidden(error)) {
    return (
      <div className={style.spotlightWrapper}>
        <div className={style.gateMessage}>
          <h2>Spotlight is not available for your account</h2>
          <p>
            This page is limited to admin accounts while it is in development. If you think you
            should have access, ask an administrator to enable it for you.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={style.spotlightWrapper}>
      <div className={style.container}>
        {isMobile && <Statistic stats={stats} />}
        {isMobile && (
          <FilterHeader
            quickFilter={quickFilter}
            setQuickFilter={setQuickFilter}
            onOpenFilter={() => setIsFilterOpen(true)}
            activeFilterCount={activeFilterCount}
          />
        )}

        <Filter
          isFilterOpen={isFilterOpen}
          setIsFilterOpen={setIsFilterOpen}
          options={options}
          optionsFailed={optionsFailed}
          filters={filters}
          stats={stats}
          activeFilterCount={activeFilterCount}
          onToggleFacet={toggleFacet}
          onSetFlag={setFlag}
          onSearch={setSearch}
          onClearFilters={clearFilters}
          onRetryOptions={() => setSideToken((n) => n + 1)}
        />

        <Companies
          companies={companies}
          totalCount={totalCount}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          error={error}
          notice={notice}
          activeFilterCount={activeFilterCount}
          onLoadMore={handleLoadMore}
          onOpenCompany={handleOpenCompany}
          onToggleSave={handleToggleSave}
          onClearFilters={clearFilters}
          onDismissNotice={() => setNotice(null)}
          onRetry={() => setReloadToken((n) => n + 1)}
        />

        {activeCompany && (
          <CompanyModal
            company={activeCompany}
            onClose={handleCloseCompany}
            onToggleSave={handleToggleSave}
            onAuthError={handleAuthError}
          />
        )}
      </div>
    </div>
  )
}

export default Spotlight
